// A deliberate sibling of ArchivedChatView: a read record, not a live room, so it forks TranscriptList rather than reusing its socket/mic props.

import React, { useMemo, useState } from "react";
import { Avatar, Input } from "antd";

import { ParticipantDisplayNames, SpeakerMap, Transcript } from "../../types/appointments/ConsultTypes";
import { TranscriptStatus } from "../../types/appointments/AppointmentTypes";
import { resolveSpeakerName, speakerRoleFor } from "../../utils/consult/speakerNames";
import { transcriptSaidAt } from "../../hooks/consult/useConsultTranscripts";
import { formatLocalTime } from "../../utils/userPrefs/DateTimeUtils";
import { TranscriptStatusTag } from "../../pages/healthcare/consultation/consultationComponents/Transcript";

interface ArchivedTranscriptViewProps {
	transcripts: Transcript[];
	speakerMap?: SpeakerMap;
	displayNames?: ParticipantDisplayNames;
	status?: TranscriptStatus;
	onSegmentSelect?: (segment: Transcript) => void;
	activeSegmentId?: string | null;
}

interface TextPart {
	text: string;
	match: boolean;
}

// Splits rather than injects HTML — the transcript is participant speech, never markup.
const highlightParts = (text: string, term: string): TextPart[] => {
	if (!term) return [{ text, match: false }];

	const lower = text.toLowerCase();
	const needle = term.toLowerCase();
	const parts: TextPart[] = [];
	let cursor = 0;

	while (cursor < text.length) {
		const index = lower.indexOf(needle, cursor);
		if (index === -1) {
			parts.push({ text: text.slice(cursor), match: false });
			break;
		}

		if (index > cursor) parts.push({ text: text.slice(cursor, index), match: false });
		parts.push({ text: text.slice(index, index + needle.length), match: true });
		cursor = index + needle.length;
	}

	return parts;
};

const ArchivedTranscriptView: React.FC<ArchivedTranscriptViewProps> = ({ transcripts, speakerMap, displayNames, status, onSegmentSelect, activeSegmentId }) => {
	const [search, setSearch] = useState<string>("");
	const term = search.trim();

	const filtered = useMemo(() => {
		if (!term) return transcripts;
		const needle = term.toLowerCase();
		return transcripts.filter((t) => t.transcript.toLowerCase().includes(needle));
	}, [transcripts, term]);

	return (
		<div data-testid="appointments-consult-transcript-log">
			<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
				<TranscriptStatusTag status={status} />
				<Input allowClear placeholder="Search transcript" data-testid="appointments-consult-transcript-search" value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 240 }} />
				{term && (
					<span style={{ fontSize: 12, color: "var(--mg-text-secondary)" }}>
						{filtered.length} of {transcripts.length}
					</span>
				)}
			</div>

			{!transcripts.length ? (
				<div data-testid="appointments-consult-transcript-empty" style={{ fontSize: 13, color: "var(--mg-text-secondary)" }}>
					No transcript was recorded for this consultation.
				</div>
			) : !filtered.length ? (
				<div data-testid="appointments-consult-transcript-no-matches" style={{ fontSize: 13, color: "var(--mg-text-secondary)" }}>
					No matches.
				</div>
			) : (
				<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
					{filtered.map((t) => {
						const role = speakerRoleFor(t, speakerMap);
						const name = resolveSpeakerName(t, speakerMap, displayNames);
						const isPractitioner = role === "practitioner";
						const selectable = Boolean(onSegmentSelect);
						const active = activeSegmentId === t.id;

						const handleActivate = () => onSegmentSelect?.(t);
						const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
							if (e.key === "Enter") handleActivate();
						};

						const avatar = (
							<Avatar size={28} style={{ flexShrink: 0, backgroundColor: "var(--mg-primary)" }}>
								{name.charAt(0)}
							</Avatar>
						);

						return (
							<div
								key={t.id}
								data-testid="appointments-consult-transcript-message"
								data-role={role ?? "unknown"}
								data-segment-id={t.id}
								style={{ display: "flex", gap: 8, justifyContent: isPractitioner ? "flex-end" : "flex-start" }}
							>
								{!isPractitioner && avatar}

								<div
									role={selectable ? "button" : undefined}
									tabIndex={selectable ? 0 : undefined}
									onClick={selectable ? handleActivate : undefined}
									onKeyDown={selectable ? handleKeyDown : undefined}
									data-active={active ? "true" : undefined}
									style={{
										maxWidth: "75%",
										padding: "8px 12px",
										borderRadius: 8,
										background: isPractitioner ? "var(--mg-bg-subtle)" : "var(--mg-bg-card)",
										border: active ? "1px solid var(--mg-primary)" : "1px solid var(--mg-border)",
										cursor: selectable ? "pointer" : "default",
									}}
								>
									<div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap", marginBottom: 4 }}>
										<span style={{ fontSize: 12, fontWeight: 500 }}>{name}</span>
										<span style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>{formatLocalTime(new Date(transcriptSaidAt(t)))}</span>
									</div>

									<div style={{ fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
										{highlightParts(t.transcript, term).map((part, index) => (part.match ? <mark key={index}>{part.text}</mark> : <React.Fragment key={index}>{part.text}</React.Fragment>))}
									</div>
								</div>

								{isPractitioner && avatar}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default ArchivedTranscriptView;
